/**
 * useAdaptiveLoop · 阶梯循环核心（从视频组件抽成纯 composable，好单测）
 *
 * 输入（都是响应式对象，会读最新值）：
 *   - state:        usePlayerState.state（ST 对象）
 *   - videoElRef:   Vue ref<HTMLVideoElement>
 *   - tracksRef:    Vue ref<{en,zh,bi}>   三条 TextTrack 引用
 *
 * 暴露函数：
 *   - seekSentence(i, play=true)   跳转某句
 *   - gotoNextSentence()           下一句（阶梯重置）
 *   - gotoPrevSentence()           上一句
 *   - applyRate(rate)              设置倍速 + 更新按钮 active
 *   - onTimeUpdate()               绑定到 video.timeupdate
 *   - highlightCurrent()           滚动高亮当前句
 *   - updateChips()                更新三个状态 chip 文案
 *   - setupPlayerTextTracks()      lesson 变化后：替换 <source>+<track>，拿 TextTrack 引用
 *   - applySubtitleMode(mode)      切换字幕（关/EN/中/双语）
 */
import { nextTick, watch } from 'vue'
import { state, toast } from './usePlayerState'

const SPEED_OPTIONS = [0.4, 0.5, 0.75, 0.8, 1.0, 1.2, 1.5, 2.0]

export function useAdaptiveLoop(videoElRef, tracksRef, els) {
  // 轮次计数器：每次 seek 回到句首（进入新一轮播放）都自增
  // 替代之前的 lastFiredEndKey 方案 —— 之前用 si-rep-stp 作去重，但 rep/stp 每轮都变，
  // 导致 lock_one / adaptive repeat 场景下去重失效、seek 被重复触发。
  // 现在以 "播放轮次" 为去重单位：一轮播放中 rng.end 只触发一次，seek 回句首才开启下一轮。
  let loopRound = 1
  let lastFiredRound = 0
  let pauseTimer = null   // 句尾停顿计时器（pause_gap > 0 时用）
  let _programmaticSeek = false  // 标记当前 seek 是代码发起的（非用户拖进度条）

  /** 清除句尾停顿计时器（用户手动操作时调用，取消待执行的 seek 回句首） */
  function clearPauseTimer() {
    if (pauseTimer) { clearTimeout(pauseTimer); pauseTimer = null }
  }

  /**
   * 代码主动 seek：设置标志位，防止 onSeeking 覆盖已有 guard。
   * onSeeking 检测到此标志后直接返回，不设新 guard。
   */
  function programmaticSeek(v, targetCT) {
    _programmaticSeek = true
    v.currentTime = targetCT
  }

  /**
   * 停顿 pause_gap 秒后 seek 回句首并播放。
   * pause_gap=0 时立即 seek（等价于原来的行为）。
   * @param {number} targetCT   seek 目标时间
   * @param {number} gap        停顿秒数
   */
  function seekBackWithGap(targetCT, gap) {
    const v = videoElRef.value
    if (!v) return
    if (gap > 0) {
      pauseTimer = setTimeout(() => {
        pauseTimer = null
        programmaticSeek(v, targetCT)
        v.play().catch(() => {})
      }, gap * 1000)
    } else {
      programmaticSeek(v, targetCT)
      v.play().catch(() => {})
    }
  }

  // 防止 "主动 v.currentTime=..." 后浏览器异步 seek 尚未生效时，
  // 紧接着的 timeupdate 里 manual-seek correction 看见 t 不匹配就把 si 改回去
  // （典型 race：NEXT_SENT 设 si=1 + seek(7.5)，但下一次 timeupdate t 仍是旧值 6.3，
  //   修正逻辑看见 6.3 < sentence1.start - 0.05，误以为用户跳了，又把 si 拉回 0）
  //
  // 大视频场景：40 分钟以上的 MP4（关键帧间隔通常 1~10s） seek 到中间位置，
  // 浏览器解码 + HTTP Range 拉流 + 关键帧对齐，可能需要 5~15 秒才真正改变 currentTime。
  // 因此把静默期改成 "按跳跃距离 + 视频时长动态"，避免 1.2s 到点后修正逻辑乱插手。
  //
  // 两层防护：
  //   ① until：主动 seek 后 {ms} 内全局跳过 correction（给浏览器足够时间 seek）
  //   ② targetSi/targetCT：当 "currentTime 正在接近我们主动 seek 的目标" 时，
  //      即便 until 到期了，也不允许 correction 把 si 从 targetSi 改走 —— 因为浏览器
  //      seek 可能刚好卡在途中，我们正往正确的地方去，别乱插手。
  //   ③ owner：区分 "用户/代码主动 seek（'manual'）" 和 "修正逻辑补偿 seek（'correct'）"，
  //      后者永远不会覆盖前者，避免「点了第400句 → 1.2s 后修正逻辑把 targetSi 改回第1句 →
  //      后续浏览器真 seek 到位时反而被判为用户手动拖动」的死锁。
  const seekGuard = { until: 0, targetSi: -1, targetCT: 0, owner: 'none' }

  /**
   * 根据跳跃距离估算一个合理的浏览器 seek 静默期（毫秒）。
   * 规则：
   *   · 距离 ≤ 10s        → 1.5s（短跳，切下一句）
   *   · 距离 10s ~ 5min    → 3s + dist/10 s（小步长调）
   *   · 距离 5min ~ 20min  → 8s
   *   · 距离 > 20min       → min(15s, 3s + dist / 200s)  长视频大跳转最多等15s
   *   · 保底 1.5s，上限 15s
   */
  function estimateSeekGuardMs(distSec, v) {
    const d = Math.max(0, +distSec || 0)
    let ms
    if (d <= 10)              ms = 1500
    else if (d <= 5 * 60)     ms = 3000 + d / 10 * 1000
    else if (d <= 20 * 60)    ms = 8000
    else                      ms = Math.min(15000, 3000 + d / 200 * 1000)
    // 如果视频 duration 有数据，但 buffered 非常少 → 正在从 HTTP Range 拉中段，
    // 这种 "冷 seek 未缓冲区" 再给 +3s 缓冲加载时间（但最多不超 15s）
    if (v && v.duration > 0 && Number.isFinite(v.duration)) {
      try {
        let bufferedSec = 0
        for (let i = 0; i < (v.buffered?.length || 0); i++) {
          bufferedSec += Math.max(0, (v.buffered.end(i) || 0) - (v.buffered.start(i) || 0))
        }
        if (bufferedSec < 30 && d > 60) ms = Math.min(15000, ms + 3000)
      } catch (_) { /* ignore */ }
    }
    return Math.max(1500, Math.min(15000, ms | 0))
  }

  /**
   * @param {object} opts
   * @param {number} [opts.targetSi]   已知目标句子下标（不知道就传 -1，比如用户拖进度条时）
   * @param {number} [opts.targetCT]   已知目标 currentTime（不知道就传 v.currentTime，浏览器正 seek 向的值）
   * @param {number} [opts.ms]         不传则按 dist 自动估算
   * @param {'manual'|'correct'} [opts.owner]  manual=用户/代码主动seek；correct=修正补偿
   */
  function kickSeekRaceGuard({ targetSi = -1, targetCT = 0, ms, owner = 'manual' } = {}) {
    const v = videoElRef.value
    // —— "correct" 绝不能覆盖正在进行的 "manual" seek —— //
    if (owner === 'correct' && seekGuard.owner === 'manual' && Date.now() < seekGuard.until) {
      // 还在 manual 的静默期内，就别用 correct 去污染 targetSi / targetCT
      // 只给它补一个独立的 until（短一点，1s），避免 correction 之后紧跟的 timeupdate 立即再修正
      seekGuard.until = Math.max(seekGuard.until, Date.now() + 1000)
      return
    }
    let guardMs = ms
    if (!guardMs) {
      const curT = v?.currentTime || 0
      const dist = Math.abs(curT - (+targetCT || 0))
      guardMs = estimateSeekGuardMs(dist, v)
    }
    seekGuard.until = Date.now() + guardMs
    seekGuard.targetSi = targetSi
    seekGuard.targetCT = +targetCT || 0
    seekGuard.owner = owner
  }

  /**
   * 用户用浏览器原生控件拖动进度条 → <video> 先触发 seeking 事件，此时 currentTime 其实就是
   * 用户拖到的目标值，但我们不知道属于哪一句（没 loaded 之前也没法算）。
   * 这里把 seekGuard 设成一个宽松的 manual-seek，让后续 3~5 次 timeupdate 里的 correction /
   * gap-skip / 句尾阶梯全部"住手"，等浏览器真正 seek 完成（seeked 后 + 200ms）再放开。
   */
  function onSeeking() {
    const v = videoElRef.value
    if (!v) return
    // 代码主动 seek（seekBackWithGap / seekSentence 等）→ 不覆盖已有 guard
    if (_programmaticSeek) {
      _programmaticSeek = false
      return
    }
    clearPauseTimer()   // 用户手动拖进度条 → 取消句尾停顿
    const ct = v.currentTime
    const dur = v.duration || 0
    // 原生进度条的单次 seek 一般 3~6s 内足够到位；拖到视频尾部则距离很短。
    const dist = Math.min(ct || 0, dur - ct || 0) || 0
    const ms = Math.max(2500, estimateSeekGuardMs(dur > 0 ? ct : 0, v))
    // 目标句子未知，但只要 seekGuard 在，correction/gap-skip/句尾阶梯都不会乱改 currentTime。
    // 过 2.5s 之后 correction 再接手——那时要么 seek 到位了，要么 currentSi 能被正确定位。
    kickSeekRaceGuard({ targetCT: ct, ms, owner: 'manual' })
  }

  function seekCorrectionAllowed(currentSi, currentT) {
    if (Date.now() < seekGuard.until) return false    // 全局静默期
    // 如果 currentSi 正是我们 seek 的目标，且 currentT 离 targetCT 还在 3s 内，
    // 说明浏览器 seek 还没结束（大视频 / 网络差 / 首次冷 seek 中段），不要乱修正
    if (seekGuard.targetSi >= 0 && currentSi === seekGuard.targetSi) {
      const dist = Math.abs(currentT - seekGuard.targetCT)
      if (dist < 3.0) return false
    }
    // 更进一步：如果 guard 的 owner=manual，targetCT 与 currentT 差 > 3s 但
    // currentT 正朝着 targetCT 单调逼近（后面再判断方向略复杂，先保守：
    // 只要 owner=manual 且 targetSi 设置过且 currentSi != targetSi，
    // 也不允许修正——除非已经超过 until+3s 的宽松窗口避免 manual seek 永远卡住）
    // → 简化实现：直接用 until 控制。上面的动态 estimateSeekGuardMs 已经给了足够时间。
    return true
  }

  // ============== 工具：句子有效起止时间（用词级时间戳跳过首尾静默） ==============
  function sentencePlayRange(s) {
    if (!s) return { start: 0, end: 0 }
    const words = (Array.isArray(s.words) ? s.words : []).filter(w => w && w.word)
    if (words.length >= 2) {
      const ws = +words[0].start || 0
      const we = +words[words.length - 1].end || 0
      return {
        start: Math.max(0, Math.min(s.start, ws) - 0.05),
        end:   Math.max(s.end, we) + 0.05,
      }
    }
    return { start: s.start, end: s.end }
  }

  // ============== 倍速 ==============
  function applyRate(rate) {
    const v = videoElRef.value
    if (!v) return
    rate = Math.max(0.25, Math.min(3, +rate))
    v.playbackRate = rate
    if (els?.activeSpeedBtn) els.activeSpeedBtn(rate)
  }

  // ============== 字幕 ==============
  function applySubtitleMode(mode) {
    state.subMode = mode
    if (els?.activeSubBtn) els.activeSubBtn(mode)
    Object.values(tracksRef.value || {}).forEach(tr => { if (tr) tr.mode = 'hidden' })
    const t = tracksRef.value || {}
    if (mode === 1 && t.en) t.en.mode = 'showing'
    if (mode === 2 && t.zh) t.zh.mode = 'showing'
    if (mode === 3 && t.bi) t.bi.mode = 'showing'
  }

  // ============== URL 归一化：老数据可能带 http://host:port，去掉只留 /public/... 避免跨域 ==============
  function _normURL(u) {
    if (typeof u !== 'string') return u
    const m = u.match(/^https?:\/\/[^/]+(\/.*)$/)
    return m ? m[1] : u
  }

  // ============== 换课：重建 <source>/<track> + 拿 TextTrack ==============
  async function setupPlayerTextTracks() {
    const v = videoElRef.value
    const m = state.lesson
    if (!v || !m) return
    v.innerHTML = ''
    // 视频源
    const src = document.createElement('source')
    src.src = _normURL(m.video_url)
    src.type = guessVideoMime(m.video_filename || m.video_url || '')
    v.appendChild(src)
    // 三条字幕轨
    const addT = (label, srcLang, url) => {
      if (!url) return null
      const t = document.createElement('track')
      t.kind = 'subtitles'; t.default = false
      t.label = label; t.srclang = srcLang; t.src = _normURL(url)
      v.appendChild(t)
      return t
    }
    addT('English', 'en', m.en_vtt_url || m.enVttUrl || m.vttUrl || m.en_srt_url)
    addT('中文',    'zh', m.zh_vtt_url || m.zhVttUrl || m.zh_srt_url)
    addT('双语',    'bi', m.bi_vtt_url || m.biVttUrl || m.bi_vtt_url)
    v.load()

    // <track> 被加入后，真实 TextTrack 在 video.textTracks 集合里，nextTick 再取
    await nextTick()
    const tt = v.textTracks; const out = { en: null, zh: null, bi: null }
    for (let i = 0; i < tt.length; i++) {
      const tr = tt[i]; tr.mode = 'hidden'
      if (tr.label === 'English') out.en = tr
      else if (tr.label === '中文') out.zh = tr
      else if (tr.label === '双语') out.bi = tr
    }
    tracksRef.value = out
    applySubtitleMode(state.subMode)
  }
  // ⚠️ watcher 已删除：state.lesson 改变时由 App.vue 的 loadLesson() 显式调用 setupPlayerTextTracks()。
  // 之前 watch + 显式调用会让 setupPlayerTextTracks 连跑两次：第一次 <source> 加进去浏览器刚
  // 开始异步 v.load()，第二次进来又 v.innerHTML='' 把 <source> 删了，于是 video.src 永远为空。

  // ============== 跳转某句 ==============
  function seekSentence(i, play = true) {
    if (!state.sentences[i]) return
    clearPauseTimer()
    state.si = i; state.stp = 0; state.rep = 0
    loopRound++   // 进入新播放轮次
    const targetCT = Math.max(0, sentencePlayRange(state.sentences[i]).start)
    kickSeekRaceGuard({ targetSi: i, targetCT })
    const v = videoElRef.value
    if (v) {
      programmaticSeek(v, targetCT)
      if (state.adaptive) applyRate(state.staircase[0]?.rate || 1.0)
      if (play) v.play().catch(() => {})
    }
    highlightCurrent(); updateChips()
  }

  // ============== UI 高亮/Chip ==============
  function highlightCurrent() {
    if (!els?.markActive) return
    els.markActive(state.si)
  }
  function updateChips() {
    if (!els?.updateChips) return
    const total = state.sentences.length
    const sent = total ? state.si + 1 : 0
    let stepTxt = '顺序播放'
    let stepOn = false
    if (state.adaptive) {
      const totalStairs = state.staircase.length
      const totalRounds = state.staircase.reduce((s, x) => s + x.repeat, 0)
      const doneRounds = state.staircase.slice(0, state.stp).reduce((s, x) => s + x.repeat, 0) + state.rep
      const st = state.staircase[state.stp]
      const gapTxt = state.pause_gap > 0 ? `  ⏸${state.pause_gap}s` : ''
      if (st) {
        stepTxt = `🪜 阶梯${state.stp + 1}/${totalStairs}  ${st.rate}× 第${state.rep + 1}/${st.repeat}轮  总计${doneRounds + 1}/${totalRounds}${gapTxt}`
      } else {
        stepTxt = `🪜 阶梯完成  总计${totalRounds}/${totalRounds}轮${gapTxt}`
      }
      stepOn = true
    }
    const lockTxt = state.lock_one ? '🔒 锁定当前句循环' : '🔓 顺序播放'
    const lockOn = state.lock_one
    els.updateChips({ sent, total, stepTxt, stepOn, lockTxt, lockOn })
  }
  // 只要 state 里的关键字段变了就刷新 chip
  watch(
    () => [state.si, state.stp, state.rep, state.adaptive, state.lock_one, state.pause_gap, state.sentences.length, state.staircase],
    updateChips, { deep: true, immediate: true }
  )

  // ============== 下一句 / 上一句 ==============
  function gotoNextSentence() {
    clearPauseTimer()
    if (state.si >= state.sentences.length - 1) {
      state.stp = 0; state.rep = 0
      toast('🎉 本课已完成')
      updateChips(); return
    }
    state.si++; state.stp = 0; state.rep = 0
    loopRound++   // 进入新播放轮次
    const targetCT = Math.max(0, sentencePlayRange(state.sentences[state.si]).start)
    kickSeekRaceGuard({ targetSi: state.si, targetCT })
    const r0 = state.staircase[0]?.rate || 1.0
    applyRate(r0)
    const v = videoElRef.value
    if (v) { programmaticSeek(v, targetCT); v.play().catch(() => {}) }
    highlightCurrent(); updateChips()
  }
  function gotoPrevSentence() {
    clearPauseTimer()
    if (state.si > 0) seekSentence(state.si - 1)
    else { seekSentence(0); if (videoElRef.value) programmaticSeek(videoElRef.value, 0) }
  }

  // ============== timeupdate 核心：阶梯状态机 ==============
  function onTimeUpdate() {
    const v = videoElRef.value
    const s = state.sentences[state.si]
    if (!v || !s) return
    // 句尾停顿期间，跳过所有 timeupdate 逻辑（等待 setTimeout 回调 seek 回句首）
    if (pauseTimer) return
    const t = v.currentTime
    const rng = sentencePlayRange(s)
    const seekingRaw = Date.now() < seekGuard.until
    // 如果视频当前时间已远离 seek guard 目标位置，即使墙钟未到期也视为 guard 已过期
    // （倍速 > 1 或用户手动大跳时，墙钟 1500ms 内视频时间可能已跑远）
    const seeking = seekingRaw && Math.abs(t - seekGuard.targetCT) < 5.0
    const _dbg = Math.floor(t * 10) % 10 === 0  // throttle: log every ~100ms
    if (_dbg) console.log(`[loop-v2] si=${state.si} stp=${state.stp} rep=${state.rep} t=${t.toFixed(2)} rng.end=${rng.end.toFixed(2)} seek=${seeking} dist=${Math.abs(t - seekGuard.targetCT).toFixed(1)} lr=${lastFiredRound} lr_guard=${loopRound}`)

    // —— 句子间隙快进：如果当前在两句之间的静默 gap（超过 0.5s），直接跳到下一句有效起点 ——
    //    注意：用户拖进度条时 (seeking=true) 绝对不要触发，否则拖完就被抢回。
    //    自适应模式下由句尾逻辑接管（重复/升级/跳句），此处不再抢先触发 gap-skip。
    if (!seeking && t > rng.end + 0.5 && !state.adaptive) {
      const next = state.sentences[state.si + 1]
      if (next) {
        const nr = sentencePlayRange(next)
        if (nr.start - rng.end > 0.5) {
          state.si++; state.stp = 0; state.rep = 0
          loopRound++   // 进入新播放轮次（新句子）
          const targetCT = Math.max(0, nr.start)
          kickSeekRaceGuard({ targetSi: state.si, targetCT })
          programmaticSeek(v, targetCT)
          applyRate(state.staircase[0]?.rate || 1.0)   // 🩹 新句子必须回到第一档倍速（之前 gap-skip 会漏重置，导致跳空档后语速仍停留在上一句最后一档）
          highlightCurrent(); updateChips()
          return
        }
      }
    }

    // 用户手动 seek 到了别的区间 → 自动修正 si（左闭右开，避免边界归属错误）
    // 提前计算 outOfCurRange，供 end-of-sentence 检查也使用：如果 t 已飘出当前句有效范围，
    // 说明用户手动拖/seek 过了，应让 correction 接管，而不是让 end-of-sentence 错误触发。
    const outOfCurRange = t < rng.start - 0.05 || t > rng.end + 0.1

    // 计算下一句的开始时间（用于判断 t 是否仍在当前句合理范围内）
    const nextSentenceStart = state.sentences[state.si + 1]
      ? sentencePlayRange(state.sentences[state.si + 1]).start
      : Infinity

    // 到达句尾（有效播放区间的 end） → 阶梯逻辑
    // ⚠️ 必须在 correction 之前检查：否则视频自然播放过句尾时，correction 会抢先
    //    把 si 改到下一句（因为 t 落在下一句归属范围内），导致阶梯/重复逻辑永远不触发。
    // ⚠️ 使用 t < endWindowLimit 替代 !outOfCurRange：给句尾逻辑一个足够大的触发窗口
    //    （直到下一句开始前都算当前句的自然句尾），避免因 timeupdate 事件间隔导致错过触发。
    // ⚠️ 保底 0.5s 窗口：当相邻句子无缝衔接时（nextSentenceStart ≤ rng.end），
    //    窗口不能被压成 0 或负数，否则句尾检测永远进不去 → 只播一遍就跳走。
    // ⚠️ 再加 (t - rng.end) < 2.0 保护：只在句尾 2 秒内触发，防止用户手动 seek 到远处后误触发。
    // 去重：同一个播放轮次只触发一次（loopRound 与 lastFiredRound 相等就跳过）
    // seeking 期间不要触发阶梯：用户拖进度条刚好停在句尾附近时，阶梯 seek 会把人强制拉回句首。
    const endWindowLimit = Math.max(nextSentenceStart, rng.end + 0.5)
    if (!seeking && t >= rng.end && lastFiredRound !== loopRound && t < endWindowLimit && (t - rng.end) < 2.0) {
      console.log(`[loop-END] si=${state.si} t=${t.toFixed(2)} stp=${state.stp} rep=${state.rep} adaptive=${state.adaptive} staircase=${JSON.stringify(state.staircase.map(s=>({r:s.rate,rp:s.repeat})))}`)
      lastFiredRound = loopRound
      v.pause()

      const loopStart = Math.max(0, rng.start)
      const gap = state.pause_gap || 0

      if (state.lock_one) {
        state.rep++
        loopRound++   // seek 回句首 → 开启新轮次
        kickSeekRaceGuard({ targetSi: state.si, targetCT: loopStart })
        seekBackWithGap(loopStart, gap)
        updateChips(); return
      }
      if (!state.adaptive) {
        if (state.si >= state.sentences.length - 1) {
          state.stp = 0; state.rep = 0
          toast('🎉 本课已完成')
          updateChips(); return
        }
        state.si++; state.stp = 0; state.rep = 0
        loopRound++   // 新句子 → 开启新轮次
        const targetCT = Math.max(0, sentencePlayRange(state.sentences[state.si]).start)
        kickSeekRaceGuard({ targetSi: state.si, targetCT })
        seekBackWithGap(targetCT, gap)
        highlightCurrent(); updateChips()
        return
      }

      // 自适应模式
      const step = state.staircase[state.stp]
      state.rep++
      if (!step) { gotoNextSentence(); return }
      if (state.rep < step.repeat) {
        console.log(`[loop-REPEAT] si=${state.si} rep=${state.rep}/${step.repeat} seeking to ${loopStart.toFixed(2)} gap=${gap}s`)
        loopRound++   // 重复同一句 → 新轮次
        kickSeekRaceGuard({ targetSi: state.si, targetCT: loopStart })
        seekBackWithGap(loopStart, gap)
        updateChips(); return
      }
      // 当前阶梯重复够了 → 升一阶梯
      state.rep = 0; state.stp++
      const nextStep = state.staircase[state.stp]
      if (nextStep) {
        applyRate(nextStep.rate)
        loopRound++   // 新阶梯重复同一句 → 新轮次
        kickSeekRaceGuard({ targetSi: state.si, targetCT: loopStart })
        seekBackWithGap(loopStart, gap)
        updateChips(); return
      }
      gotoNextSentence()
    }

    // 用户手动 seek 到了别的区间 → 自动修正 si（左闭右开，避免边界归属错误）
    // ⚠️ 刚主动 seek 后 + 浏览器正在 seek 向目标 → 跳过，避免乱改
    if (seekCorrectionAllowed(state.si, t) && outOfCurRange) {
      let found = -1
      // 向前 / 向后扩展归属范围：两句之间的 gap 也归到"后一句"，这样用户拖到静默段不会再
      // 被 correction 判为 found=-1 进而被 gap-skip 抢 seek（gap-skip 只有 si 没动时才触发）
      for (let i = 0; i < state.sentences.length; i++) {
        const r2 = sentencePlayRange(state.sentences[i])
        const prev = i > 0 ? sentencePlayRange(state.sentences[i - 1]) : { start: -1e9, end: -1e9 }
        const gapStart = i === 0 ? -1e9 : prev.end
        // 归属起点 = 前一句结束（两句之间的 gap 归本句）；第 0 句从 -∞ 开始
        const left = Math.min(r2.start, gapStart) - 0.05
        const right = r2.end + 0.05
        if (t >= left && t < right) { found = i; break }
      }
      if (found >= 0 && found !== state.si) {
        state.si = found; state.stp = 0; state.rep = 0
        loopRound++
        // 归属到了新句子，但**不要再主动 v.currentTime=句首**——用户明确拖到了 gap 或句中，
        // 就停在用户拖的位置。否则用户拖到句子末尾 1s，又被抢回句首，观感还是"拖不动"。
        kickSeekRaceGuard({
          targetSi: found,
          targetCT: t,          // 目标就是用户拖到的 currentTime
          owner: 'correct',
          ms: 1200,             // correction 设一个短静默，避免连续修正
        })
        if (state.adaptive) applyRate(state.staircase[0]?.rate || 1.0)   // 🩹 用户手动拖到别的句子，倍速也回到第一档，避免残留上一句的阶梯速度
        highlightCurrent(); updateChips()
        return
      }
    }
  }

  return {
    SPEED_OPTIONS,
    applyRate, applySubtitleMode,
    setupPlayerTextTracks,
    seekSentence, gotoNextSentence, gotoPrevSentence,
    onSeeking, onTimeUpdate, highlightCurrent, updateChips,
  }
}

function guessVideoMime(path) {
  if (/\.mp4/i.test(path)) return 'video/mp4'
  if (/\.webm/i.test(path)) return 'video/webm'
  if (/\.mov/i.test(path)) return 'video/quicktime'
  if (/\.mkv/i.test(path)) return 'video/x-matroska'
  return 'video/mp4'
}
